/**
 * AppState — the explicit shared-state container replacing v1's 27 webpack
 * exports-object "globals" (SPEC.md §3; research/01 §2.1/§2.4).
 *
 * Semantics are identical by construction: one shared mutable object whose
 * property reads/writes are visible to every module. Names, types and initial
 * values are v1 fact (SPEC.md §3.3 table); only the sharing is now declared.
 *
 * Module-eval side effects reproduce v1 global_variables.js order
 * (REQ-BOOT-1a): the localStorage "cpc_configData" rehydrate through a
 * parseJSON that returns `undefined` for falsy input followed by `|| {}`
 * (research/01 Q5), then `window.cpc = window.cpc || {}`.
 */

/** The slice of a Shopify product.js variant entry the DIV-026 size-matched
 * variant resolver needs — see `checkout/variant-resolution.ts`. */
export interface ProductVariantOptionSummary {
  id: number;
  option1?: string | null;
  option2?: string | null;
  option3?: string | null;
  available?: boolean | null; // DIV-042: product.js `available`; false = Shopify won't sell it
}

export interface AppState {
  cpc_currentPrice: number | string; // string after buildPriceMarker default (research/03 §14)
  miniMumCartValue: number | string; // becomes a STRING via toFixed (research/04 §7.14, REQ-STATE-2)
  priceMarkerDecimals: number;
  baseVariantId: number;
  cpc_calculatorId: string;
  cpc_waitforcartconfirmation: boolean;
  cpc_isNewCheckoutFlowEnabled: boolean;
  cpc_cartDrawerEnabled: boolean;
  readonly baseCalculatorNewApiUrl: string; // __BASE_API_URL__
  calculatorStyling: any; // StoreStylingDTO | null
  cpc_data: any; // CalculatorData — {} until load
  cpc_configData: any; // ConfigurationDTO | {}
  readonly spaceRegex: RegExp; // new RegExp(" ", "g")
  readonly specialCharactersRegex: RegExp; // /[^\w\s]/gi
  cpc_shopUrl: string; // default "apippa" (research/01 Q17)
  cpc_product_default_price: number;
  // DIV-032: the boot-time variant's `available` flag (product.js), resolved
  // alongside cpc_product_default_price/baseVariantId (DIV-029). null when no
  // product JSON has resolved yet, or the theme's variant entry has no
  // `available` field at all — see find-product-form.ts's use of this.
  cpc_product_default_available: boolean | null;
  cpc_pageLoaded: boolean;
  cpc_isPreview: boolean;
  cpc_productId: number;
  readonly cpc_metaFields: any[]; // MUTATED IN PLACE, never reassigned (REQ-STATE-1)
  productForm: HTMLFormElement | null | undefined; // undefined until wiring
  cpc_fasterCheckoutConfig: any; // StoreAppConfigResponse | null
  isError: boolean; // shared validation flag (research/05 §3, REQ-STATE-3)
  readonly cdnURL: string; // __CDN_URL__
  addToCartSelector: string;
  cpc_isCheckoutInProgress: boolean;
  nativeCurrencyEnabled: boolean; // #chunk3: client-side Shopify.currency conversion (currency v1)
  cpc_translations: Record<string, string> | null; // translations v1: source → translated text for the shopper's locale, null = none
  cpc_translations_rtl: boolean; // translations v1: the served language reads right-to-left (derived from the map, see i18n/translate)
  readonly cpc_productOptionNames: string[]; // DIV-026: product.js `options` (option names, positional with variant option1/2/3). MUTATED IN PLACE, never reassigned.
  readonly cpc_productVariants: ProductVariantOptionSummary[]; // DIV-026: full product.js `variants` (id + option1/2/3), not just variants[0]. MUTATED IN PLACE, never reassigned.
}

// v1 parseJSON verbatim (global_variables.js:30-38): returns undefined when
// inputString is falsy — the `|| {}` below supplies the default (Q5).
function parseJSON(inputString: string | null, fallback: any): any {
  if (inputString) {
    try {
      return JSON.parse(inputString);
    } catch (e) {
      return fallback;
    }
  }
}

export const appState: AppState = {
  cpc_currentPrice: 0,
  miniMumCartValue: 0,
  priceMarkerDecimals: 0,
  baseVariantId: 0,
  cpc_calculatorId: "",
  cpc_waitforcartconfirmation: true,
  cpc_isNewCheckoutFlowEnabled: false,
  cpc_cartDrawerEnabled: false,
  baseCalculatorNewApiUrl: __BASE_API_URL__,
  calculatorStyling: null,
  cpc_data: {},
  cpc_configData: parseJSON(localStorage.getItem("cpc_configData"), {}) || {},
  spaceRegex: new RegExp(" ", "g"),
  specialCharactersRegex: /[^\w\s]/gi,
  cpc_shopUrl: "apippa",
  cpc_product_default_price: 0.0,
  cpc_product_default_available: null,
  cpc_pageLoaded: false,
  cpc_isPreview: false,
  cpc_productId: 0,
  cpc_metaFields: [],
  productForm: undefined,
  cpc_fasterCheckoutConfig: null,
  isError: false,
  cdnURL: __CDN_URL__,
  addToCartSelector: "",
  cpc_isCheckoutInProgress: false,
  nativeCurrencyEnabled: false,
  cpc_translations: null,
  cpc_translations_rtl: false,
  cpc_productOptionNames: [],
  cpc_productVariants: [],
};

// REQ-STATE: the 27 properties are NOT window globals (REQ-STATE-4); only the
// cpc namespace object is ensured here, exactly like v1 (research/01 §2.1).
window.cpc = window.cpc || {};
